import React, { useEffect, useState } from 'react';
import { Result, Button, Spin, Typography, Card } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// ─── /payment/checkout?session_id=...&order_id=...&subscription_id=... ────────
// OR  /payment/checkout?subscription_id=...  (fetches session from backend first)
// Loads the Cashfree JS SDK and initiates the payment drop.
// The Cashfree SDK script is loaded dynamically to keep the bundle lean.
export const PaymentCheckout = () => {
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [errorMsg, setErrorMsg] = useState('');

    const params = new URLSearchParams(window.location.search);
    let sessionId = params.get('session_id');
    let orderId = params.get('order_id');
    const subscriptionId = params.get('subscription_id');

    useEffect(() => {
        if (!subscriptionId) {
            setStatus('error');
            setErrorMsg('Invalid payment link. Please contact support@baideac.com.');
            return;
        }

        if (sessionId && orderId) {
            // Session already provided in URL – load SDK immediately
            loadCashfreeSDK(() => initiateCashfree(sessionId));
        } else {
            // Only subscription_id present – fetch a fresh session from the backend first
            fetch(
                `${process.env.REACT_APP_API_BASE}/api/payment/get-session/${encodeURIComponent(subscriptionId)}`
            )
                .then((r) => {
                    if (!r.ok) return r.json().then((e) => Promise.reject(e));
                    return r.json();
                })
                .then((data) => {
                    sessionId = data.paymentSessionId;
                    orderId = data.orderId;
                    loadCashfreeSDK(() => initiateCashfree(sessionId));
                })
                .catch((err) => {
                    setStatus('error');
                    setErrorMsg(err?.message || 'Could not initiate payment. Please contact support.');
                });
        }
    }, []);

    const loadCashfreeSDK = (onLoad) => {
        if (document.querySelector('script[src*="cashfree"]')) {
            // Already loaded
            onLoad();
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = onLoad;
        script.onerror = () => {
            setStatus('error');
            setErrorMsg('Failed to load payment gateway. Please try again.');
        };
        document.head.appendChild(script);
    };

    const initiateCashfree = (sid) => {
        try {
            // eslint-disable-next-line no-undef
            const cashfree = Cashfree({ mode: process.env.REACT_APP_CASHFREE_ENV || 'sandbox' });
            setStatus('ready');
            cashfree.checkout({ paymentSessionId: sid, redirectTarget: '_self' }).then((result) => {
                if (result.error) {
                    setStatus('error');
                    setErrorMsg(result.error.message || 'Payment failed. Please try again.');
                }
            });
        } catch {
            setStatus('error');
            setErrorMsg('Could not initialize payment. Please try again or contact support.');
        }
    };

    if (status === 'loading') {
        return (
            <div style={styles.center}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                <Title level={4} style={{ marginTop: 24 }}>Loading Payment Gateway…</Title>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div style={styles.center}>
                <Result
                    status="error"
                    title="Payment Error"
                    subTitle={errorMsg}
                    extra={
                        <Button type="primary" href="mailto:support@baideac.com">
                            Contact Support
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={styles.center}>
            <Card style={{ maxWidth: 480, width: '100%' }}>
                <Title level={3} style={{ textAlign: 'center' }}>Complete Payment</Title>
                <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
                    Order ID: <strong>{orderId}</strong>
                </Text>
                <Spin indicator={<LoadingOutlined spin />} />
                <Text style={{ display: 'block', textAlign: 'center', marginTop: 12 }}>
                    Redirecting to Cashfree checkout…
                </Text>
            </Card>
        </div>
    );
};

// ─── /payment/result?order_id=...&subscription_id=... ─────────────────────────
// Cashfree redirects here after payment. Polls backend for final status.
export const PaymentResult = () => {
    const [status, setStatus] = useState('checking'); // 'checking' | 'paid' | 'pending' | 'failed'
    const [expirationDate, setExpirationDate] = useState(null);

    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');

    useEffect(() => {
        if (!orderId) {
            setStatus('failed');
            return;
        }
        pollPaymentStatus();
    }, []);

    const pollPaymentStatus = async (attempts = 0) => {
        if (attempts > 8) {
            // After ~16 s give up polling and tell user to wait
            setStatus('pending');
            return;
        }
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE}/api/payment/status/${encodeURIComponent(orderId)}`
            );
            const data = await res.json();

            if (data.paymentStatus === 'paid') {
                setStatus('paid');
                setExpirationDate(data.expirationDate);
            } else if (data.paymentStatus === 'failed') {
                setStatus('failed');
            } else {
                // Still pending – retry after 2 s
                setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
            }
        } catch {
            setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
    };

    if (status === 'checking') {
        return (
            <div style={styles.center}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                <Title level={4} style={{ marginTop: 24 }}>Verifying Payment…</Title>
            </div>
        );
    }

    if (status === 'paid') {
        const expiry = expirationDate ? new Date(expirationDate).toLocaleDateString('en-IN') : '1 year from today';
        return (
            <div style={styles.center}>
                <Result
                    icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title="Payment Successful!"
                    subTitle={`Your license has been renewed and is valid until ${expiry}. You will receive a confirmation email shortly.`}
                    extra={
                        <Button type="primary" href="mailto:support@baideac.com">
                            Contact Support
                        </Button>
                    }
                />
            </div>
        );
    }

    if (status === 'pending') {
        return (
            <div style={styles.center}>
                <Result
                    status="info"
                    title="Payment Under Review"
                    subTitle="Your payment is being processed. Your license will be activated automatically once confirmed. If it takes more than 24 hours, contact support."
                    extra={
                        <Button type="primary" href="mailto:support@baideac.com">
                            Contact Support
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={styles.center}>
            <Result
                icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                title="Payment Failed"
                subTitle="Your payment could not be processed. Please try again or contact support."
                extra={
                    <Button type="primary" href="mailto:support@baideac.com">
                        Contact Support
                    </Button>
                }
            />
        </div>
    );
};

const styles = {
    center: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
    },
};
